import React, { lazy, Suspense, useMemo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import Loading from "./components/generic/Loading";
import NotFoundPage from "./components/generic/NotFound";
import GlobalTracker from "./functions/globalTracker";
import { isTokenExpired } from "./functions/token";
import "../src/styles/pageTransitions.css";
import { Tracking } from "./components/pages/Tracking";

/* Lazy page groups (grouped logically to help code-splitting) */
const Login = lazy(() => import("./components/pages/Login"));
const Dashboard = lazy(() => import("./components/pages/Dashboard"));
const PDGO = lazy(() => import("./components/pages/PD&GO"));
const StoreSales = lazy(() => import("./components/pages/StoreSales"));
const FuelSales = lazy(() => import("./components/pages/FuelSales"));
const ShiftSchedule = lazy(() => import("./components/pages/ShiftSchedule"));
const Auditorias = lazy(() => import("./components/pages/Auditorias"));
const UpsellingGlobal = lazy(() =>
  import("./components/pages/Upselling").then((m) => ({
    default: m.UpsellingGlobal,
  }))
);
const Team = lazy(() =>
  import("./components/pages/Team").then((m) => ({ default: m.Team }))
);
const Profile = lazy(() =>
  import("./components/pages/Profile").then((m) => ({ default: m.Profile }))
);
const CreateUser = lazy(() =>
  import("./components/pages/Profile").then((m) => ({ default: m.CreateUser }))
);
const EditUser = lazy(() =>
  import("./components/pages/Profile").then((m) => ({ default: m.EditUser }))
);
const MisteryClient = lazy(() =>
  import("./components/pages/MisteryClient").then((m) => ({
    default: m.MisteryClient,
  }))
);
const EditMisteryClient = lazy(() =>
  import("./components/pages/MisteryClient").then((m) => ({
    default: m.EditMisteryClient,
  }))
);
const MixUltimate = lazy(() =>
  import("./components/pages/MixUltimate").then((m) => ({
    default: m.MixTopBar,
  }))
);
const ComunicationBoard = lazy(() =>
  import("./components/pages/ComunicationBoard").then((m) => ({
    default: m.ComunicationBoard,
  }))
);
const VideoExercise = lazy(() =>
  import("./components/pages/ComunicationBoard").then((m) => ({
    default: m.VideoExercise,
  }))
);
const Controler = lazy(() =>
  import("./components/pages/Controler").then((m) => ({
    default: m.Controller,
  }))
);
const Messages = lazy(() =>
  import("./components/pages/Messages").then((m) => ({ default: m.Messages }))
);
const CreateMessage = lazy(() =>
  import("./components/pages/Messages").then((m) => ({
    default: m.CreateMessage,
  }))
);
const EditMessage = lazy(() =>
  import("./components/pages/Messages").then((m) => ({
    default: m.EditMessage,
  }))
);
const AssociateMessage = lazy(() =>
  import("./components/pages/Messages").then((m) => ({
    default: m.AssociateMessageToShop,
  }))
);

/* Configuration (large module – keep together) */
const Configuration = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.Configuration,
  }))
);
const Users = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({ default: m.Users }))
);
const Imports = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.Imports,
  }))
);
const Shops = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({ default: m.Shops }))
);
const MisteryClientImport = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.MisteryClientImport,
  }))
);
const CreateMisteryClient = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.CreateMisteryClient,
  }))
);
const AssociateMisteryClientToShop = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.AssociateMisteryClientToShop,
  }))
);
const MisteryClientEvaluation = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.MisteryClientEvaluation,
  }))
);
const CreateMisteryClientEvaluation = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.CreateMisteryClientEvaluation,
  }))
);
const EditMisteryClientEvaluation = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.EditMisteryClientEvaluation,
  }))
);
const Upselling = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.Upselling,
  }))
);
const UpsellingData = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.UpsellingData,
  }))
);
const CreateUpsellingData = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.CreateUpsellingData,
  }))
);
const EditUpsellingData = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.EditUpsellingData,
  }))
);
const CreateCampaign = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.CreateCampaign,
  }))
);
const EditCampaign = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.EditCampaign,
  }))
);
const AssociateShopCampaign = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.AssociateShopCampaign,
  }))
);
const Ranking = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.CreateRank,
  }))
);
const MainPermissions = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.MainPermissions,
  }))
);
const AssignPermision = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.AssignPermision,
  }))
);
const ViewRoles = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.ViewRoles,
  }))
);
const AssociatePermissionToRole = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.AssociatePermissionToRole,
  }))
);
const AssignRole = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.AssignRole,
  }))
);
const ConfigComunicationBoard = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.ConfigComunicationBoard,
  }))
);
const InsertFileComunication = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.InserFileComunication,
  }))
);
const InsertAssociationComunication = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.InsertAssociationFile,
  }))
);
const InsertAssociationComunicationSolo = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.InsertAssociation,
  }))
);
const Marketing = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.MarketingFiles,
  }))
);
const CreateMarketingFile = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.CreateMarketingFiles,
  }))
);
const EditMarketingFile = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.EditMarketingFiles,
  }))
);
const AuditoriasConfiguration = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.AuditoriasConfiguration,
  }))
);
const CreateAudit = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.CreateAudit,
  }))
);
const EditAudit = lazy(() =>
  import("./components/pages/Configuration").then((m) => ({
    default: m.EditAudit,
  }))
);

const CreateShop = lazy(() =>
  import("./components/pages/Shops").then((m) => ({
    default: m.CreateShop,
  }))
);
const EditShop = lazy(() =>
  import("./components/pages/Shops").then((m) => ({
    default: m.EditShop,
  }))
);

/* Training */
const Training = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({ default: m.Training }))
);
const Courses = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({ default: m.Courses }))
);
const CreateEvent = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.CreateEvent,
  }))
);
const EditEvent = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({ default: m.EditEvent }))
);
const CreateQuestion = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.CreateQuestion,
  }))
);
const QuestionCourses = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.QuestionCourses,
  }))
);
const EditQuestion = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.EditQuestion,
  }))
);
const ControlRoom = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.ControlRoom,
  }))
);
const ResultsRoom = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.ResultsRoom,
  }))
);
const CreateCourse = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.CreateCourse,
  }))
);
const EditCourse = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.EditCourse,
  }))
);
const CreateModulo = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.CreateModulo,
  }))
);
const EditModulo = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.EditModulo,
  }))
);
const CreateUnity = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.CreateUnity,
  }))
);
const EditUnity = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({ default: m.EditUnity }))
);
const CreateVideoExercise = lazy(() =>
  import("./components/pages/Trainning").then((m) => ({
    default: m.CreateVideoExercise,
  }))
);
const SimulationPOS = lazy(() =>
  import("./components/pages/SimulationPOS").then((m) => ({
    default: m.SimulationPOS,
  }))
);

/* Presencial */
const Presencial = lazy(() =>
  import("./components/pages/Presencial").then((m) => ({
    default: m.Presencial,
  }))
);
const Inscricao = lazy(() =>
  import("./components/pages/Presencial").then((m) => ({
    default: m.Inscricao,
  }))
);
const Acreditacao = lazy(() =>
  import("./components/pages/Presencial").then((m) => ({
    default: m.Acreditacao,
  }))
);
const Verificar = lazy(() =>
  import("./components/pages/Presencial").then((m) => ({
    default: m.Verificar,
  }))
);

/* Utility components */
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const token = localStorage.getItem("accessToken");
  if (!token || isTokenExpired(token)) {
    localStorage.removeItem("accessToken");
    localStorage.setItem("lastPage", location.pathname);
    return <Navigate to="/Login" replace />;
  }
  return <>{children}</>;
};

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  // Route definitions (flat + nested config)
  const routes = useMemo(
    () => [
      { path: "/Login", element: <Login /> },
      { path: "/", private: true, element: <Dashboard /> },
      { path: "/Dashboard", private: true, element: <Dashboard /> },
      { path: "/PD&GO", private: true, element: <PDGO /> },
      { path: "/StoreSales", private: true, element: <StoreSales /> },
      { path: "/FuelSales", private: true, element: <FuelSales /> },
      { path: "/Upselling", private: true, element: <UpsellingGlobal /> },
      {
        path: "/Upselling/Global",
        private: true,
        element: <UpsellingGlobal />,
      },
      { path: "/Team", private: true, element: <Team /> },
      { path: "/Schedule", private: true, element: <ShiftSchedule /> },
      { path: "/Profile", private: true, element: <Profile /> },
      {
        path: "/CommunicationBoard",
        private: true,
        element: <ComunicationBoard />,
      },
      {
        path: "/VideoExercise/:enrollmentId",
        private: true,
        element: <VideoExercise />,
      },
      { path: "/Controler", private: true, element: <Controler /> },
      { path: "/Messages", private: true, element: <Messages /> },
      { path: "/CreateMessages", private: true, element: <CreateMessage /> },
      {
        path: "/EditMessage/:internalId",
        private: true,
        element: <EditMessage />,
      },
      {
        path: "/AssociateMessage/:internalId",
        private: true,
        element: <AssociateMessage />,
      },
      { path: "/MisteryClient", private: true, element: <MisteryClient /> },
      { path: "/MixUltimate", private: true, element: <MixUltimate /> },
      { path: "/Tracking", element: <Tracking /> },
      // Training
      {
        path: "/Training",
        private: true,
        element: <Navigate to="/Training/Courses" replace />,
      },
      { path: "/Training/Courses", private: true, element: <Training /> },
      {
        path: "/Training/CreateEvent",
        private: true,
        element: <CreateEvent />,
      },
      {
        path: "/Training/EditEvent/:id",
        private: true,
        element: <EditEvent />,
      },
      {
        path: "/Training/CreateQuestion",
        private: true,
        element: <CreateQuestion />,
      },
      {
        path: "/Training/QuestionCourses/:internalId",
        private: true,
        element: <QuestionCourses />,
      },
      {
        path: "/Training/EditQuestion/:internalId",
        private: true,
        element: <EditQuestion />,
      },
      {
        path: "/Training/ControlRoom/:id",
        private: true,
        element: <ControlRoom />,
      },
      {
        path: "/Training/ResultsRoom/:id",
        private: true,
        element: <ResultsRoom />,
      },
      {
        path: "/Training/CreateCourse",
        private: true,
        element: <CreateCourse />,
      },
      {
        path: "/Training/EditCourse/:internalId",
        private: true,
        element: <EditCourse />,
      },
      {
        path: "/Training/CreateModulo",
        private: true,
        element: <CreateModulo />,
      },
      {
        path: "/Training/EditModulo/:internalId",
        private: true,
        element: <EditModulo />,
      },
      {
        path: "/Training/CreateUnity",
        private: true,
        element: <CreateUnity />,
      },
      {
        path: "/Training/EditUnity/:internalId",
        private: true,
        element: <EditUnity />,
      },
      {
        path: "/Training/CreateVideoExercise",
        private: true,
        element: <CreateVideoExercise />,
      },
    ],
    []
  );

  return (
    <TransitionGroup component={null}>
      <Routes location={location}>
        {/* Config nested tree */}
        <Route
          path="/Config"
          element={
            <PrivateRoute>
              <Configuration />
            </PrivateRoute>
          }
        >
          <Route
            path="Users"
            element={
              <PrivateRoute>
                <Users />
              </PrivateRoute>
            }
          />
          <Route
            path="CreateUsers"
            element={
              <PrivateRoute>
                <CreateUser />
              </PrivateRoute>
            }
          />
          <Route
            path="EditUsers/:internalId"
            element={
              <PrivateRoute>
                <EditUser />
              </PrivateRoute>
            }
          />
          <Route
            path="Imports"
            element={
              <PrivateRoute>
                <Imports />
              </PrivateRoute>
            }
          />
          <Route
            path="Shops"
            element={
              <PrivateRoute>
                <Shops />
              </PrivateRoute>
            }
          />
          <Route
            path="MisteryClient"
            element={
              <PrivateRoute>
                <MisteryClientImport />
              </PrivateRoute>
            }
          />
          <Route
            path="CreateMisteryClient"
            element={
              <PrivateRoute>
                <CreateMisteryClient />
              </PrivateRoute>
            }
          />
          <Route
            path="AssociateShop"
            element={
              <PrivateRoute>
                <AssociateMisteryClientToShop />
              </PrivateRoute>
            }
          />
          <Route
            path="EditMisteryClient/:internalId"
            element={
              <PrivateRoute>
                <EditMisteryClient />
              </PrivateRoute>
            }
          />
          <Route
            path="MisteryClientEvaluation"
            element={
              <PrivateRoute>
                <MisteryClientEvaluation />
              </PrivateRoute>
            }
          />
          <Route
            path="CreateEvaluation"
            element={
              <PrivateRoute>
                <CreateMisteryClientEvaluation />
              </PrivateRoute>
            }
          />
          <Route
            path="EditEvaluation/:id"
            element={
              <PrivateRoute>
                <EditMisteryClientEvaluation />
              </PrivateRoute>
            }
          />
          <Route
            path="Upselling"
            element={
              <PrivateRoute>
                <Upselling />
              </PrivateRoute>
            }
          />
          <Route
            path="UpsellingData"
            element={
              <PrivateRoute>
                <UpsellingData />
              </PrivateRoute>
            }
          />
          <Route
            path="CreateUpsellingData"
            element={
              <PrivateRoute>
                <CreateUpsellingData />
              </PrivateRoute>
            }
          />
          <Route
            path="EditUpsellingData"
            element={
              <PrivateRoute>
                <EditUpsellingData />
              </PrivateRoute>
            }
          />
          <Route
            path="CreateCampaign"
            element={
              <PrivateRoute>
                <CreateCampaign />
              </PrivateRoute>
            }
          />
          <Route
            path="EditCampaign/:internalId"
            element={
              <PrivateRoute>
                <EditCampaign />
              </PrivateRoute>
            }
          />
          <Route
            path="AssociateShopCampaign"
            element={
              <PrivateRoute>
                <AssociateShopCampaign />
              </PrivateRoute>
            }
          />
          <Route
            path="UpsellingRanking"
            element={
              <PrivateRoute>
                <Ranking />
              </PrivateRoute>
            }
          />
          <Route
            path="CreateShop"
            element={
              <PrivateRoute>
                <CreateShop />
              </PrivateRoute>
            }
          />
          <Route
            path="EditShop/:internalId"
            element={
              <PrivateRoute>
                <EditShop />
              </PrivateRoute>
            }
          />
          <Route
            path="Permissions"
            element={
              <PrivateRoute>
                <MainPermissions />
              </PrivateRoute>
            }
          />
          <Route
            path="CreatePermissions"
            element={
              <PrivateRoute>
                <AssignPermision />
              </PrivateRoute>
            }
          />
          <Route
            path="ViewRoles"
            element={
              <PrivateRoute>
                <ViewRoles />
              </PrivateRoute>
            }
          />
          <Route
            path="PermissionToRole"
            element={
              <PrivateRoute>
                <AssociatePermissionToRole />
              </PrivateRoute>
            }
          />
          <Route
            path="AssignRole"
            element={
              <PrivateRoute>
                <AssignRole />
              </PrivateRoute>
            }
          />
          <Route
            path="ConfigComunication"
            element={
              <PrivateRoute>
                <ConfigComunicationBoard />
              </PrivateRoute>
            }
          />
          <Route
            path="InsertFile"
            element={
              <PrivateRoute>
                <InsertFileComunication />
              </PrivateRoute>
            }
          />
          <Route
            path="AssociateFileMany"
            element={
              <PrivateRoute>
                <InsertAssociationComunication />
              </PrivateRoute>
            }
          />
          <Route
            path="AssociateFile"
            element={
              <PrivateRoute>
                <InsertAssociationComunicationSolo />
              </PrivateRoute>
            }
          />
          <Route
            path="Marketing"
            element={
              <PrivateRoute>
                <Marketing />
              </PrivateRoute>
            }
          />
          <Route
            path="CreateMarketingFile"
            element={
              <PrivateRoute>
                <CreateMarketingFile />
              </PrivateRoute>
            }
          />
          <Route
            path="EditMarketingFile/:internalId"
            element={
              <PrivateRoute>
                <EditMarketingFile />
              </PrivateRoute>
            }
          />
          <Route
            path="Auditorias"
            element={
              <PrivateRoute>
                <AuditoriasConfiguration />
              </PrivateRoute>
            }
          />
          <Route
            path="CreateAudit"
            element={
              <PrivateRoute>
                <CreateAudit />
              </PrivateRoute>
            }
          />
          <Route
            path="EditAudit/:internalId"
            element={
              <PrivateRoute>
                <EditAudit />
              </PrivateRoute>
            }
          />
        </Route>

        {/* Presencial nested */}
        <Route
          path="Presencial"
          element={
            <PrivateRoute>
              <Presencial />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="Inscricao" replace />} />
          <Route
            path="Inscricao"
            element={
              <PrivateRoute>
                <Inscricao />
              </PrivateRoute>
            }
          />
          <Route
            path="Acreditacao"
            element={
              <PrivateRoute>
                <Acreditacao />
              </PrivateRoute>
            }
          />
          <Route
            path="Verificar"
            element={
              <PrivateRoute>
                <Verificar />
              </PrivateRoute>
            }
          />
        </Route>
        <Route
          path="SimulationPOS"
          element={
            <PrivateRoute>
              <SimulationPOS />
            </PrivateRoute>
          }
        />

        {/* Flat routes */}
        {routes.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={
              r.private ? <PrivateRoute>{r.element}</PrivateRoute> : r.element
            }
          />
        ))}

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </TransitionGroup>
  );
};

function App() {
  return (
    <BrowserRouter>
      <GlobalTracker />
      <Suspense fallback={<Loading />}>
        <AnimatedRoutes />
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
